import type { ISerialize } from '@/objects/ISerialize';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { DeviceAntenna } from './DeviceAntenna';
import { DeviceBattery } from './DeviceBattery';
import { DeviceMessage } from './DeviceMessage';
import { DeviceGpioRead } from './DeviceGpioRead';
import type { DeviceType } from './../enums/DeviceType';
import { formatDateIso } from '@/utilities/dateutils';

export class DeviceStatus implements ISerialize {
    public Antennas: DeviceAntenna[];

    public Batteries: DeviceBattery[];

    public Channel: string | null;

    public Connected: boolean | null;

    public DeviceId: string | null;

    public DeviceType: DeviceType;

    public HasMessageAlert: boolean;

    public IsStream: boolean;

    public LastPush: Date | null;

    public LastSeen: Date | null;

    public LastTag: string | null;

    public LastTagTime: Date | null;

    public Messages: DeviceMessage[];

    public NetworkStrength: number | null;

    public OnBattery: boolean | null;

    public readonly ReceiveOffset: ITimeSpan | null;

    public RecentGpioReads: DeviceGpioRead[];

    public Time: Date;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Antennas?: DeviceAntenna[];
        Batteries?: DeviceBattery[];
        Channel?: string | null;
        Connected?: boolean | null;
        DeviceId?: string | null;
        DeviceType?: DeviceType;
        HasMessageAlert?: boolean;
        IsStream?: boolean;
        LastPush?: Date | null;
        LastSeen?: Date | null;
        LastTag?: string | null;
        LastTagTime?: Date | null;
        Messages?: DeviceMessage[];
        NetworkStrength?: number | null;
        OnBattery?: boolean | null;
        ReceiveOffset?: ITimeSpan | null;
        RecentGpioReads?: DeviceGpioRead[];
        Time?: Date;
    }) {
        this.Antennas = values.Antennas ?? [];

        this.Batteries = values.Batteries ?? [];

        this.Channel = values.Channel ?? null;

        this.Connected = values.Connected ?? null;

        this.DeviceId = values.DeviceId ?? null;

        this.DeviceType = values.DeviceType ?? 'Unknown';

        this.HasMessageAlert = values.HasMessageAlert ?? false;

        this.IsStream = values.IsStream ?? false;

        this.LastPush = values.LastPush ?? null;

        this.LastSeen = values.LastSeen ?? null;

        this.LastTag = values.LastTag ?? null;

        this.LastTagTime = values.LastTagTime ?? null;

        this.Messages = values.Messages ?? [];

        this.NetworkStrength = values.NetworkStrength ?? null;

        this.OnBattery = values.OnBattery ?? null;

        this.ReceiveOffset = values.ReceiveOffset ?? null;

        this.RecentGpioReads = values.RecentGpioReads ?? [];

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
    }

    public serialize(): string {
        return (
            '{' +
            `"Antennas": [ ${this.Antennas.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Batteries": [ ${this.Batteries.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Channel": ${JSON.stringify(this.Channel)}` +
            ',' +
            `"Connected": ${JSON.stringify(this.Connected)}` +
            ',' +
            `"DeviceId": ${JSON.stringify(this.DeviceId)}` +
            ',' +
            `"DeviceType": ${this.DeviceType == 'Unknown' ? 0 : this.DeviceType == 'MiniTrack' ? 1 : this.DeviceType == 'MiniTrack_VIA_TimeRecorder' ? 2 : this.DeviceType == 'EQMobile' ? 3 : this.DeviceType == 'TimeRecorder' ? 4 : this.DeviceType == 'OldTimeRecorder' ? 5 : this.DeviceType == 'ETS_ECB' ? 6 : this.DeviceType == 'ETS_ECB_VIA_TimeRecorder' ? 7 : this.DeviceType == 'Ubidium' ? 8 : this.DeviceType == 'VIA_EMIT' ? 9 : this.DeviceType == 'ECU_VIA_TimeRecorder' ? 10 : 0}` +
            ',' +
            `"HasMessageAlert": ${JSON.stringify(this.HasMessageAlert)}` +
            ',' +
            `"IsStream": ${JSON.stringify(this.IsStream)}` +
            ',' +
            `"LastPush": ${this.LastPush ? '"' + formatDateIso(this.LastPush) + '"' : null}` +
            ',' +
            `"LastSeen": ${this.LastSeen ? '"' + formatDateIso(this.LastSeen) + '"' : null}` +
            ',' +
            `"LastTag": ${JSON.stringify(this.LastTag)}` +
            ',' +
            `"LastTagTime": ${this.LastTagTime ? '"' + formatDateIso(this.LastTagTime) + '"' : null}` +
            ',' +
            `"Messages": [ ${this.Messages.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"NetworkStrength": ${JSON.stringify(this.NetworkStrength)}` +
            ',' +
            `"OnBattery": ${JSON.stringify(this.OnBattery)}` +
            ',' +
            `"ReceiveOffset": ${this.ReceiveOffset ? '"' + this.ReceiveOffset?.serialize() + '"' : null}` +
            ',' +
            `"RecentGpioReads": [ ${this.RecentGpioReads.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            '}'
        );
    }

    /**
     * Creates an instance of DeviceStatus from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Antennas?: DeviceAntenna[];
        Batteries?: DeviceBattery[];
        Channel?: string | null;
        Connected?: boolean | null;
        DeviceId?: string | null;
        DeviceType?: DeviceType;
        HasMessageAlert?: boolean;
        IsStream?: boolean;
        LastPush?: Date | null;
        LastSeen?: Date | null;
        LastTag?: string | null;
        LastTagTime?: Date | null;
        Messages?: DeviceMessage[];
        NetworkStrength?: number | null;
        OnBattery?: boolean | null;
        RecentGpioReads?: DeviceGpioRead[];
        Time?: Date;
    }): DeviceStatus {
        return new DeviceStatus(values);
    }

    public static _FromServerJson(json: any): DeviceStatus {
        return new DeviceStatus({
            Antennas: json.Antennas?.map((x: any) => DeviceAntenna._FromServerJson(x)),
            Batteries: json.Batteries?.map((x: any) => DeviceBattery._FromServerJson(x)),
            Channel: json.Channel ?? '',
            Connected: json.Connected ?? null,
            DeviceId: json.DeviceId ?? '',
            DeviceType:
                json.DeviceType == 0
                    ? 'Unknown'
                    : json.DeviceType == 1
                      ? 'MiniTrack'
                      : json.DeviceType == 2
                        ? 'MiniTrack_VIA_TimeRecorder'
                        : json.DeviceType == 3
                          ? 'EQMobile'
                          : json.DeviceType == 4
                            ? 'TimeRecorder'
                            : json.DeviceType == 5
                              ? 'OldTimeRecorder'
                              : json.DeviceType == 6
                                ? 'ETS_ECB'
                                : json.DeviceType == 7
                                  ? 'ETS_ECB_VIA_TimeRecorder'
                                  : json.DeviceType == 8
                                    ? 'Ubidium'
                                    : json.DeviceType == 9
                                      ? 'VIA_EMIT'
                                      : json.DeviceType == 10
                                        ? 'ECU_VIA_TimeRecorder'
                                        : 'Unknown',
            HasMessageAlert: json.HasMessageAlert,
            IsStream: json.IsStream,
            LastPush: json.LastPush ? (json.LastPush instanceof Date ? json.LastPush : new Date(json.LastPush)) : null,
            LastSeen: json.LastSeen ? (json.LastSeen instanceof Date ? json.LastSeen : new Date(json.LastSeen)) : null,
            LastTag: json.LastTag ?? '',
            LastTagTime: json.LastTagTime ? (json.LastTagTime instanceof Date ? json.LastTagTime : new Date(json.LastTagTime)) : null,
            Messages: json.Messages?.map((x: any) => DeviceMessage._FromServerJson(x)),
            NetworkStrength: json.NetworkStrength ?? null,
            OnBattery: json.OnBattery ?? null,
            ReceiveOffset: json.ReceiveOffset ? TimeSpan.FromTimeString(json.ReceiveOffset) : null,
            RecentGpioReads: json.RecentGpioReads?.map((x: any) => DeviceGpioRead._FromServerJson(x)),
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
        });
    }

    /**
     * Creates an instance of DeviceStatus from another instance of DeviceStatus.
     * @param other The DeviceStatus you want to clone
     * @returns A cloned DeviceStatus
     */
    public static FromOther(other: DeviceStatus): DeviceStatus {
        return DeviceStatus._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return DeviceStatus.FromOther(this);
    }

    static NAME_OF_CLASS = 'DeviceStatus';
}
